import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import FlashIcon from "@hugeicons/core-free-icons/FlashIcon";
import UserGroupIcon from "@hugeicons/core-free-icons/UserGroupIcon";
import Shield01Icon from "@hugeicons/core-free-icons/Shield01Icon";
import Infinity01Icon from "@hugeicons/core-free-icons/Infinity01Icon";
import LockPasswordIcon from "@hugeicons/core-free-icons/LockPasswordIcon";
import ArrowRight01Icon from "@hugeicons/core-free-icons/ArrowRight01Icon";
import AlarmClockIcon from "@hugeicons/core-free-icons/AlarmClockIcon";
import Wallet02Icon from "@hugeicons/core-free-icons/Wallet02Icon";

type LifetimeAccessPricingProps = {
  spotsLeft?: number;
  totalSpots?: number;
  price?: number;
  originalMonthlyPrice?: number;
  className?: string;
  onGetAccess?: () => void;
};

export function LifetimeAccessPricing({
  spotsLeft = 67,
  totalSpots = 100,
  price = 200,
  originalMonthlyPrice = 20,
  className,
  onGetAccess,
}: LifetimeAccessPricingProps) {
  const progressPercentage = ((totalSpots - spotsLeft) / totalSpots) * 100;

  return (
    <section
      className={cn(
        "relative mx-auto w-full max-w-7xl px-2 py-12 sm:px-4 sm:py-16 lg:py-20",
        className
      )}
    >
      <div className="overflow-hidden rounded-3xl border border-border/30 bg-card dark:bg-[#0d0d0d] shadow-sm">
        <div className="grid gap-0 lg:grid-cols-2">
          {/* Left Side - Content */}
          <div className="flex flex-col justify-between p-5 sm:p-8 lg:p-12">
            <div>
              {/* Badge */}
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-accent/30 px-4 py-2">
                <HugeiconsIcon
                  icon={FlashIcon}
                  size={14}
                  className="text-brand-accent"
                />
                <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                  Limited-Time Lifetime Deal
                </span>
              </div>

              {/* Heading */}
              <h2 className="mb-4 text-3xl font-black leading-[1.1] tracking-tight text-foreground sm:mb-5 sm:text-5xl lg:text-[3.5rem]">
                Never Pay
                <br />
                Monthly{" "}
                <span className="text-brand-accent">Again.</span>
              </h2>

              {/* Description */}
              <p className="mb-6 max-w-md text-sm leading-relaxed text-muted-foreground sm:mb-8 sm:text-lg">
                One payment.{" "}
                <span className="font-semibold text-foreground">
                  Lifetime access.
                </span>
                <br />
                All future updates included.
              </p>

              {/* Feature Icons Row */}
              <div className="mb-6 grid grid-cols-3 gap-3 sm:mb-8 sm:gap-6">
                <div className="flex flex-col items-center gap-2 text-center sm:gap-3">
                  <div className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-muted/50 dark:border-border/30 dark:bg-white/5 sm:size-12">
                    <HugeiconsIcon
                      icon={Infinity01Icon}
                      size={20}
                      className="text-brand-accent sm:[&>svg]:size-[22px]"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-accent sm:text-sm">
                      Lifetime Access
                    </p>
                    <p className="hidden text-xs text-muted-foreground sm:block">
                      Yours forever.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 text-center sm:gap-3">
                  <div className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-muted/50 dark:border-border/30 dark:bg-white/5 sm:size-12">
                    <HugeiconsIcon
                      icon={FlashIcon}
                      size={20}
                      className="text-brand-accent sm:[&>svg]:size-[22px]"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-accent sm:text-sm">
                      Future Updates
                    </p>
                    <p className="hidden text-xs text-muted-foreground sm:block">
                      Always included.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 text-center sm:gap-3">
                  <div className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-muted/50 dark:border-border/30 dark:bg-white/5 sm:size-12">
                    <HugeiconsIcon
                      icon={Shield01Icon}
                      size={20}
                      className="text-brand-accent sm:[&>svg]:size-[22px]"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-accent sm:text-sm">
                      No Monthly Fees
                    </p>
                    <p className="hidden text-xs text-muted-foreground sm:block">
                      Pay once. Never again.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div className="rounded-xl border border-border/60 bg-muted/40 p-3 dark:border-border/20 dark:bg-white/[0.03] sm:p-4">
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Avatar Stack */}
                <div className="flex -space-x-2 shrink-0 sm:-space-x-2.5">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="relative size-7 overflow-hidden rounded-full ring-2 ring-card dark:ring-[#0d0d0d] sm:size-9"
                    >
                      <img
                        src={`/square-users/squser${i}.png`}
                        alt=""
                        className="size-full object-cover bg-muted"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-foreground sm:text-sm">
                    Trusted by{" "}
                    <span className="font-bold text-brand-accent">5,000+</span>
                  </p>
                  <p className="text-xs text-muted-foreground sm:text-sm">
                    creators and businesses
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Pricing Card */}
          <div className="flex flex-col p-4 sm:p-8 lg:p-10">
            <div className="flex h-full flex-col rounded-2xl border border-brand-accent/15 bg-muted/30 p-5 shadow-[0_4px_32px_-8px_var(--brand-accent-light)] dark:border-brand-accent/10 dark:bg-white/[0.03] dark:shadow-[0_4px_32px_-8px_color-mix(in_srgb,var(--brand-accent)_25%,transparent)] sm:p-8">
              {/* Slots Counter */}
              <div className="mb-4 flex items-center justify-center gap-2">
                <HugeiconsIcon
                  icon={UserGroupIcon}
                  size={18}
                  className="text-brand-accent"
                />
                <span className="text-sm font-bold text-brand-accent">
                  {spotsLeft}
                </span>
                <span className="text-sm text-muted-foreground">
                  / {totalSpots} slots left
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted dark:bg-white/10">
                  <div
                    className="h-full rounded-full bg-brand-accent transition-all duration-700 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              {/* Price Display */}
              <div className="mb-1 text-center">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-accent">
                  Lifetime Deal
                </p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-black tracking-tight text-foreground sm:text-7xl">
                    ${price}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  one-time payment
                </p>
              </div>

              {/* Savings */}
              <div className="mb-5 mt-4 text-center">
                <p className="text-lg font-bold text-foreground">
                  Save{" "}
                  <span className="text-brand-accent">$240</span>{" "}
                  / year
                </p>
              </div>

              {/* Pay Once Badge */}
              <div className="mb-5 flex justify-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/50 px-4 py-2 dark:border-border/20 dark:bg-white/5">
                  <HugeiconsIcon
                    icon={Wallet02Icon}
                    size={16}
                    className="text-brand-accent"
                  />
                  <span className="text-sm font-semibold text-brand-accent">
                    Pay Once. Never Renew.
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mb-5 flex justify-center">
                <Button
                  onClick={onGetAccess}
                  className="h-12 w-full rounded-full bg-primary-gradient px-6 text-sm font-bold text-primary-foreground shadow-md shadow-primary/20 transition-all hover:brightness-[0.97] hover:-translate-y-0.5 hover:shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                >
                  <HugeiconsIcon
                    icon={LockPasswordIcon}
                    size={18}
                    className="mr-2"
                  />
                  Claim Lifetime Access
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    size={18}
                    className="ml-2"
                  />
                </Button>
              </div>

              {/* Guarantee */}
              <div className="mb-4 flex items-center justify-center gap-2">
                <HugeiconsIcon
                  icon={Shield01Icon}
                  size={15}
                  className="text-muted-foreground"
                />
                <span className="text-sm text-muted-foreground">
                  30-Day Money-Back Guarantee
                </span>
              </div>

              {/* Divider */}
              <hr className="mb-4 border-border/40" />

              {/* Limited Time Notice */}
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <HugeiconsIcon
                  icon={AlarmClockIcon}
                  size={22}
                  className="shrink-0 text-brand-accent sm:[&>svg]:size-[28px]"
                />
                <span className="text-xs text-muted-foreground sm:text-sm">
                  This lifetime deal is available for a{" "}
                  <span className="font-semibold text-brand-accent">
                    limited time
                  </span>{" "}
                  only.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LifetimeAccessPricing;
